import type { AppLocale } from '@/shared/i18n'
import type { RaidCalendarDay, RaidCalendarWeek, RaidSummary, RaidStatus } from '../types/index.ts'
import {
  MAX_NAME_LEN,
  MAX_RAID_DURATION_MINUTES,
  MIN_RAID_DURATION_MINUTES,
  RaidStatus as RaidStatusValue,
} from '../types/index.ts'

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Day-of-week index the planner treats as the first day of the week.
 * `0` is Sunday, `1` is Monday — the same numbering `Date#getDay()`
 * returns, so callers can compose `weekdayColumnIndex(date, weekStartsOn)`
 * directly off `date.getDay()` without an extra lookup.
 *
 * Localised rather than Intl-derived on purpose: `Intl.DateTimeFormat#weekInfo`
 * (the proper `weekStartsOn` hook) is unevenly supported across the engines
 * the front-end targets (a known gap in older Safari builds), and the
 * planner only ships two locales today, so a small explicit lookup is
 * both narrower in scope and easier to test deterministically than
 * wrapping an Intl API.
 *
 * Both `en` and `ru` resolve to Monday-first (`1`). The lookup is
 * still locale-keyed (rather than hard-coded to `1`) so adding a
 * locale that diverges — e.g. en-US, where Sunday-first is the
 * historical default — stays a one-line change.
 */
export type WeekStartsOn = 0 | 1

const WEEK_STARTS_ON_BY_LOCALE: Readonly<Record<AppLocale, WeekStartsOn>> = Object.freeze({
  en: 1,
  ru: 1,
})

/** Resolve the effective calendar week-start for the application locale. */
export function weekStartsOnFor(locale: AppLocale): WeekStartsOn {
  return WEEK_STARTS_ON_BY_LOCALE[locale]
}

/**
 * Default time-slot granularity for the calendar. The planner surfaces raid
 * starts at 15-minute boundaries — shorter than the schedule modal's
 * `:00/:15/:30/:45` picker — so users can drop a raid into a Google-Calendar-
 * style grid at quarter-hour increments. Mirrored by the
 * `<input type="time" step="900">` in `RaidScheduleModal` (900s = 15min).
 */
export const TIME_SLOT_MINUTES = 15

/**
 * Snap a `Date` to the nearest 15-minute boundary. Returns a new `Date` with
 * seconds/milliseconds zeroed and minutes rounded to the nearest multiple of
 * 15 (with carry into hours when rounding lands at 60). Used by both the
 * calendar (so click-to-create lands on a clean grid line) and the schedule
 * modal (so the default value of the time input is grid-aligned).
 *
 * `Math.round` ties at exactly 7.5 → 15 (i.e. halfway between slots). The
 * carry math is plain `getMinutes()` + `setMinutes()`, so DST changes
 * (60-minute shifts) still resolve correctly because `setHours` and
 * `setMinutes` are DST-aware on the underlying `Date`.
 */
export function snapTo15Minutes(date: Date): Date {
  const next = new Date(date.getTime())
  next.setSeconds(0, 0)
  const minutes = next.getMinutes()
  const snapped = Math.round(minutes / TIME_SLOT_MINUTES) * TIME_SLOT_MINUTES
  if (snapped === 60) {
    next.setHours(next.getHours() + 1, 0, 0, 0)
  } else {
    next.setMinutes(snapped, 0, 0)
  }
  return next
}

export function clampRaidDuration(value: number): number {
  if (!Number.isFinite(value)) return MIN_RAID_DURATION_MINUTES
  return Math.min(
    MAX_RAID_DURATION_MINUTES,
    Math.max(MIN_RAID_DURATION_MINUTES, Math.round(value)),
  )
}

export function isValidRaidName(name: string): boolean {
  const trimmed = name.trim()
  return trimmed.length > 0 && Array.from(trimmed).length <= MAX_NAME_LEN
}

export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/** Normalize a date to the first day of its local calendar month. */
export function startOfLocalMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

/** Move a calendar-month cursor without carrying an invalid day into the next month. */
export function addLocalMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1)
}

/** Replace the month of a calendar-month cursor while retaining its year. */
export function withLocalMonth(date: Date, month: number): Date {
  return new Date(date.getFullYear(), month, 1)
}

/** Replace the year of a calendar-month cursor while retaining its month. */
export function withLocalYear(date: Date, year: number): Date {
  const next = startOfLocalMonth(date)
  next.setFullYear(year)
  return next
}

/**
 * Start of the local-time week that contains `date`, walking backwards to
 * the most recent `weekStartsOn`-day. Pure local-time arithmetic — no DST
 * surprise because we step one calendar day at a time and `setDate` re-runs
 * on the underlying `Date`.
 *
 * `weekStartsOn = 1` keeps the historical Monday-first behaviour; `0` falls
 * back to a Sunday-first week (kept available so future locales that
 * diverge from Monday can opt in without changing the helper). The
 * caller is expected to derive `weekStartsOn` from the active
 * `AppLocale` via `weekStartsOnFor`.
 */
export function startOfLocalWeek(date: Date, weekStartsOn: WeekStartsOn = 1): Date {
  const day = startOfLocalDay(date)
  const offset = (day.getDay() - weekStartsOn + 7) % 7
  day.setDate(day.getDate() - offset)
  return day
}

/**
 * Column index `[0..6]` of `date` within a localized week starting on
 * `weekStartsOn`. Mirrors the indexing the grid uses for `dayIndex`,
 * so a host can keep `focusedDay` and the array index in lock-step
 * without re-deriving the modulo dance at every call site.
 */
export function weekdayColumnIndex(
  date: Date,
  weekStartsOn: WeekStartsOn = 1,
): number {
  return (date.getDay() - weekStartsOn + 7) % 7
}

export function addLocalDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

export interface MiniCalendarDay {
  readonly date: Date
  readonly isInMonth: boolean
}

/**
 * Fixed six-row month grid used by the compact navigator. The first
 * column's day-of-week is `weekStartsOn`, so the host must pass the
 * locale-derived `WeekStartsOn` to keep the mini grid's column ordering
 * in lock-step with the main calendar's day headers.
 */
export function buildMiniCalendarMonth(
  month: Date,
  weekStartsOn: WeekStartsOn = 1,
): ReadonlyArray<MiniCalendarDay> {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1)
  const startsOn = startOfLocalWeek(firstDay, weekStartsOn)
  return Array.from({ length: 42 }, (_, index) => {
    const date = addLocalDays(startsOn, index)
    return {
      date,
      isInMonth: date.getMonth() === firstDay.getMonth(),
    }
  })
}

export function isSameLocalDay(left: Date, right: Date): boolean {
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
}

export function raidsForLocalDay(
  raids: ReadonlyArray<RaidSummary>,
  date: Date,
): ReadonlyArray<RaidSummary> {
  const start = startOfLocalDay(date).getTime()
  const end = addLocalDays(startOfLocalDay(date), 1).getTime()
  return raids.filter((raid) => {
    if (raid.scheduledStartsAt === null) return false
    const scheduled = new Date(raid.scheduledStartsAt).getTime()
    return Number.isFinite(scheduled) && scheduled >= start && scheduled < end
  })
}

export function raidStatus(raid: RaidSummary, now: Date = new Date()): RaidStatus {
  if (raid.cancelledAt !== null) return RaidStatusValue.Cancelled
  if (raid.completedAt !== null) return RaidStatusValue.Completed
  if (raid.scheduledStartsAt === null) return RaidStatusValue.Scheduled

  const start = new Date(raid.scheduledStartsAt).getTime()
  if (!Number.isFinite(start)) return RaidStatusValue.Scheduled
  const duration = clampRaidDuration(raid.durationMinutes ?? 30)
  const end = start + duration * 60 * 1000
  const current = now.getTime()
  if (current < start) return RaidStatusValue.Scheduled
  if (current <= end) return RaidStatusValue.InProgress
  return RaidStatusValue.Overdue
}

/** Match the API's write lock: terminal raids and raids past their end are historical. */
export function isHistoricalRaid(raid: RaidSummary, now: Date = new Date()): boolean {
  const status = raidStatus(raid, now)
  return status === RaidStatusValue.Cancelled
    || status === RaidStatusValue.Completed
    || status === RaidStatusValue.Overdue
}

/**
 * Snap `startsOn` to the active locale's week-start and lay out the seven
 * day columns in week order. The grid consumes `days` directly, so the
 * column ordering (and the date at `days[0]`) is the locale-aware one.
 */
export function buildRaidCalendarWeek(
  startsOn: Date,
  raids: ReadonlyArray<RaidSummary>,
  weekStartsOn: WeekStartsOn = 1,
): RaidCalendarWeek {
  const weekStart = startOfLocalWeek(startsOn, weekStartsOn)
  const days: ReadonlyArray<RaidCalendarDay> = Array.from({ length: 7 }, (_, index) => {
    const date = addLocalDays(weekStart, index)
    return { date, raids: raidsForLocalDay(raids, date) }
  })
  return { startsOn: weekStart, days }
}

/**
 * Validate a persisted week-start ISO string against the current locale.
 * Returning `raw is string` only when the round-trip agrees means callers
 * can hand a `(string, AppLocale)` pair to `JSON.parse` without fearing a
 * persisted ISO whose week-start disagrees with the active locale
 * resurfacing. The helper stays free of any locale-port lookup so it
 * remains pure and deterministically testable.
 */
export function isPersistedCalendarWeek(
  raw: unknown,
  weekStartsOn: WeekStartsOn = 1,
): raw is string {
  if (typeof raw !== 'string') return false
  const parsed = new Date(raw)
  if (!Number.isFinite(parsed.getTime())) return false
  return startOfLocalWeek(parsed, weekStartsOn).toISOString() === raw
}

/**
 * Exclusive end of the week window for GraphQL `from`/`to` arguments. The
 * caller threads the same `weekStartsOn` they used to anchor the window
 * so the half-open `[startsOn, endsOn)` range stays consistent regardless
 * of which week-start the active locale picks — a future locale that
 * diverges from Monday flows through this thread without a separate code
 * path.
 */
export function calendarWindowEnd(
  startsOn: Date,
  weekStartsOn: WeekStartsOn = 1,
): Date {
  return new Date(startOfLocalWeek(startsOn, weekStartsOn).getTime() + 7 * DAY_MS)
}
